import { useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { library, users } from '@cwos/api-client';
import { Button } from '@cwos/ui-admin';
import {
  askPhrasesFor,
  bindingnessLabel,
  bindingnessSentence,
  extractionKindLabel,
  suggestSubjectKeys,
  topicSubjectKey,
} from './libraryLogic.js';

/**
 * PROPOSE A READING — the step between "we hold this document" and "this is
 * our rule".
 *
 * A person picks a passage (or writes the rule out) and says what it means:
 * the rule in plain words, the topic it answers, how binding it is, and who
 * reviews it. That goes to the existing `POST /knowledge/extractions`, which
 * raises a review task for the chosen role. NOTHING HERE IS IN FORCE: the
 * reading becomes a rule only when an approver accepts it on the Approvals tab,
 * choosing its start date, and the API refuses the proposer approving it
 * themselves whenever someone else could.
 *
 * THE TOPIC IS THE JOIN KEY FOR ASK PHIBIAN. It is folded into a subject key by
 * `topicSubjectKey`, which mirrors the server's own folding, and the form shows
 * the exact phrases a question must contain for Ask to find this rule.
 *
 * MODEL-DRAFTED READINGS (owner ruling 2026-10-07) are `PhibianDrafts.tsx`:
 * the server drafts from the whole document and files each draft as a pending
 * reading through the same route this form uses. Review, approval and the
 * two-person rule do not change, and no path makes a draft effective without a
 * person approving it. */

export interface ReadingDraft {
  readonly statement?: string;
  readonly topic?: string;
  readonly kind?: library.ExtractionKind;
  readonly bindingness?: library.Bindingness;
}

const KINDS: readonly library.ExtractionKind[] = [
  'rule',
  'definition',
  'responsibility',
  'workflow',
];
const ALL_BINDINGNESS: readonly library.Bindingness[] = [
  'hard_constraint',
  'strong_default',
  'guidance',
  'informational',
];

export interface ProposeReadingProps {
  /**
   * The document the reading comes from — or, for a rule written by hand, a
   * function that stores the text as a document first, so a manual rule goes
   * through the same review as one taken from a file.
   */
  source: string | ((statement: string) => Promise<string>);
  initial?: ReadingDraft;
  statementLabel?: string;
  bindingChoices?: readonly library.Bindingness[];
  submitLabel?: string;
  onProposed: (extraction: library.Extraction, sourceId: string) => void;
}

export function ProposeReading({
  source,
  initial,
  statementLabel = 'What it says, as a rule',
  bindingChoices = ALL_BINDINGNESS,
  submitLabel = 'Send for review',
  onProposed,
}: ProposeReadingProps) {
  const queryClient = useQueryClient();
  const [statement, setStatement] = useState(initial?.statement ?? '');
  const [topic, setTopic] = useState(initial?.topic ?? '');
  const [kind, setKind] = useState<library.ExtractionKind>(initial?.kind ?? 'rule');
  const [bindingness, setBindingness] = useState<library.Bindingness>(
    initial?.bindingness ?? bindingChoices[0] ?? 'guidance',
  );
  const [reviewerRoleId, setReviewerRoleId] = useState('');
  // A hand-written rule's document is stored ONCE per text: a retry after a
  // failed proposal reuses it, but a retry after editing the rule stores the
  // edited words, so the document always says what the reading says.
  const storedSource = useRef<{ text: string; id: string } | null>(null);

  const roles = useQuery({ queryKey: ['roles', 'list'], queryFn: () => users.roles() });
  const roleOptions = roles.data ?? [];
  // Default reviewer: the organization's administrators.
  const reviewer =
    reviewerRoleId || (roleOptions.find((r) => r.key === 'org_admin') ?? roleOptions[0])?.id || '';

  const subject = topicSubjectKey(topic);
  const phrases = subject === null ? [] : askPhrasesFor(subject);
  const suggestions = suggestSubjectKeys(statement).filter((k) => k !== subject);

  const propose = useMutation({
    mutationFn: async () => {
      const text = statement.trim();
      let sourceId: string;
      if (typeof source === 'string') sourceId = source;
      else if (storedSource.current?.text === text) sourceId = storedSource.current.id;
      else {
        sourceId = await source(text);
        storedSource.current = { text, id: sourceId };
      }
      const extraction = await library.proposeExtraction({
        sourceId,
        kind,
        subject: subject!,
        proposedStatement: text,
        proposedBindingness: bindingness,
        reviewerRoleId: reviewer,
      });
      return { extraction, sourceId };
    },
    onSuccess: ({ extraction, sourceId }) => {
      void queryClient.invalidateQueries({ queryKey: ['library'] });
      storedSource.current = null;
      setStatement('');
      setTopic('');
      onProposed(extraction, sourceId);
    },
  });

  const ready =
    statement.trim() !== '' && subject !== null && phrases.length > 0 && reviewer !== '';

  return (
    <form
      className="rn-col kl-upload"
      onSubmit={(e) => {
        e.preventDefault();
        if (ready && !propose.isPending) propose.mutate();
      }}
    >
      <label className="rn-col kl-field">
        <span className="rn-strong">{statementLabel}</span>
        <textarea
          className="kl-paste"
          rows={4}
          value={statement}
          onChange={(e) => setStatement(e.target.value)}
          placeholder="A worker visiting a client alone for the first time checks in with the on-call supervisor before the visit."
        />
      </label>

      <label className="rn-col kl-field">
        <span className="rn-strong">Topic</span>
        <input
          value={topic}
          onChange={(e) => setTopic(e.target.value)}
          placeholder="Lone worker"
          aria-describedby="kl-topic-help"
        />
        <span id="kl-topic-help" className="rn-muted">
          {subject === null
            ? 'One or two words that name what this is about, like “lone worker” or “mileage”.'
            : phrases.length === 0
              ? 'Too general for Ask Phibian to find. Add a specific word, like “client > intake”.'
              : `Ask Phibian finds this when a question mentions ${phrases
                  .map((p) => `“${p}”`)
                  .join(' or ')}.`}
        </span>
        {suggestions.length > 0 ? (
          <span className="rn-row kl-topics">
            {suggestions.map((key) => (
              <button
                key={key}
                type="button"
                className="kl-topic-chip"
                onClick={() => setTopic(askPhrasesFor(key).join(' > '))}
              >
                {askPhrasesFor(key).join(' › ')}
              </button>
            ))}
          </span>
        ) : null}
      </label>

      <div className="kl-meta-grid">
        <label className="rn-col kl-field">
          <span className="rn-strong">What it is</span>
          <select value={kind} onChange={(e) => setKind(e.target.value as library.ExtractionKind)}>
            {KINDS.map((k) => (
              <option key={k} value={k}>
                {extractionKindLabel(k)}
              </option>
            ))}
          </select>
        </label>
        <label className="rn-col kl-field">
          <span className="rn-strong">How binding</span>
          <select
            value={bindingness}
            onChange={(e) => setBindingness(e.target.value as library.Bindingness)}
          >
            {bindingChoices.map((b) => (
              <option key={b} value={b}>
                {bindingnessLabel(b)}
              </option>
            ))}
          </select>
          <span className="rn-muted">{bindingnessSentence(bindingness)}</span>
        </label>
        <label className="rn-col kl-field">
          <span className="rn-strong">Who reviews it</span>
          <select
            value={reviewer}
            onChange={(e) => setReviewerRoleId(e.target.value)}
            disabled={roleOptions.length === 0}
          >
            {roleOptions.length === 0 ? (
              <option value="">
                {roles.isLoading ? 'Loading roles…' : 'No roles could be loaded'}
              </option>
            ) : null}
            {roleOptions.map((r) => (
              <option key={r.id} value={r.id}>
                {r.name}
              </option>
            ))}
          </select>
        </label>
      </div>

      {propose.isError ? (
        <p className="rn-muted kl-error" role="alert">
          {propose.error instanceof Error
            ? propose.error.message
            : 'That reading could not be sent for review. Nothing was saved.'}
        </p>
      ) : null}

      <div className="rn-row kl-actions">
        <Button type="submit" variant="primary" disabled={!ready || propose.isPending}>
          {propose.isPending ? 'Sending…' : submitLabel}
        </Button>
        <span className="rn-muted">
          Not in force until an approver accepts it and sets the date it starts.
        </span>
      </div>
    </form>
  );
}
